import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HFS_LOGO = 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/qxumdsjc_HFS_logo_192x192.png';

const Header = () => (
  <div className="flex items-center justify-center gap-3 py-6">
    <img src={HFS_LOGO} alt="HFS" className="h-12 w-12 rounded-lg" data-testid="hfs-logo" />
    <span className="text-2xl font-bold text-slate-800">HireForSkillz</span>
  </div>
);

export default function PublicAssessmentPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [stage, setStage] = useState('details'); // details | test | done
  const [candidate, setCandidate] = useState({ name: '', email: '', phone: '' });
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const submittedRef = useRef(false);

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        const res = await axios.get(`${API}/assessment/public/${slug}`);
        setAssessment(res.data);
        setAnswers(new Array(res.data.questions.length).fill(-1));
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchAssessment();
  }, [slug]);

  const handleSubmit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        name: candidate.name,
        email: candidate.email,
        phone: candidate.phone,
        answers: answers.map((a) => (a === -1 ? -1 : a)),
      };
      await axios.post(`${API}/assessment/public/${slug}/submit`, payload);
      setStage('done');
    } catch (e) {
      submittedRef.current = false;
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [answers, candidate, slug]);

  // Countdown timer
  useEffect(() => {
    if (stage !== 'test' || timeLeft === null) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [stage, timeLeft, handleSubmit]);

  const startTest = () => {
    if (!candidate.name.trim() || !candidate.email.trim() || !candidate.phone.trim()) {
      alert('Please fill in all your details.');
      return;
    }
    if (assessment.duration_minutes) {
      setTimeLeft(assessment.duration_minutes * 60);
    }
    setStage('test');
  };

  const selectOption = (qIndex, optIndex) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[qIndex] = optIndex;
      return next;
    });
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md" data-testid="assessment-not-found">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Assessment Unavailable</h1>
            <p className="text-slate-500">This assessment link is invalid or no longer active. Please contact the team for assistance.</p>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-10" data-testid="assessment-thankyou">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-5" />
            <h1 className="text-2xl font-bold text-slate-800 mb-3">Thank you for your response.</h1>
            <p className="text-slate-500 text-base">Your response is recorded. Get in touch with the team for the results.</p>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'details') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4 pb-10">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <h1 className="text-2xl font-bold text-slate-800 mb-1" data-testid="assessment-title">{assessment.title}</h1>
            {assessment.description && <p className="text-slate-500 mb-4">{assessment.description}</p>}
            <div className="flex flex-wrap gap-3 mb-6 text-sm">
              <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600">{assessment.questions.length} Questions</span>
              {assessment.duration_minutes && (
                <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> {assessment.duration_minutes} min time limit
                </span>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="cand-name">Full Name *</Label>
                <Input id="cand-name" data-testid="candidate-name-input" value={candidate.name}
                  onChange={(e) => setCandidate({ ...candidate, name: e.target.value })} placeholder="Your full name" />
              </div>
              <div>
                <Label htmlFor="cand-email">Email *</Label>
                <Input id="cand-email" type="email" data-testid="candidate-email-input" value={candidate.email}
                  onChange={(e) => setCandidate({ ...candidate, email: e.target.value })} placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="cand-phone">Phone *</Label>
                <Input id="cand-phone" data-testid="candidate-phone-input" value={candidate.phone}
                  onChange={(e) => setCandidate({ ...candidate, phone: e.target.value })} placeholder="Your phone number" />
              </div>
            </div>
            <Button onClick={startTest} className="w-full mt-6 bg-slate-800 hover:bg-slate-900" data-testid="start-assessment-btn">
              Start Assessment
            </Button>
            {assessment.duration_minutes && (
              <p className="text-xs text-slate-400 text-center mt-3">Once you start, the timer cannot be paused.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // test stage
  const answeredCount = answers.filter((a) => a !== -1).length;
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={HFS_LOGO} alt="HFS" className="h-8 w-8 rounded" />
            <span className="font-semibold text-slate-800 hidden sm:inline">{assessment.title}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500" data-testid="answered-count">{answeredCount}/{answers.length} answered</span>
            {timeLeft !== null && (
              <span className={`flex items-center gap-1 font-mono font-semibold px-3 py-1 rounded-full ${timeLeft < 60 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-700'}`} data-testid="countdown-timer">
                <Clock className="h-4 w-4" /> {fmtTime(timeLeft)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-4 py-6 space-y-5">
        {assessment.questions.map((q, qi) => (
          <div key={qi} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`question-card-${qi}`}>
            <p className="font-medium text-slate-800 mb-4"><span className="text-slate-400 mr-2">Q{qi + 1}.</span>{q.question}</p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => (
                <button key={oi} onClick={() => selectOption(qi, oi)}
                  data-testid={`question-${qi}-option-${oi}`}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                    answers[qi] === oi ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 hover:border-slate-400 text-slate-700'
                  }`}>
                  <span className="font-semibold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                </button>
              ))}
            </div>
          </div>
        ))}

        <Button onClick={handleSubmit} disabled={submitting}
          className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base" data-testid="submit-assessment-btn">
          {submitting ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Submitting...</> : 'Submit Assessment'}
        </Button>
        {answeredCount < answers.length && (
          <p className="text-xs text-amber-600 text-center">You have {answers.length - answeredCount} unanswered question(s). Unanswered questions will be marked incorrect.</p>
        )}
      </div>
    </div>
  );
}
